import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { ApiResponse, LabelValue } from '../../_model/api-response';
import {
  ComplianceActivityLimitDto,
  ComplianceCatalogBootstrapDto,
  ComplianceDocumentTemplateDto,
  ComplianceFieldTemplateDto,
  ComplianceJurisdictionDto,
  ComplianceRuleTemplateDto,
  ComplianceTierDetailsDto,
  ComplianceTierDto,
  ComplianceTierTableDto,
  UserKycPolicySnapshotDetailsDto,
  UserKycPolicySnapshotDto
} from '../../_model/compliance';
import { ComplianceFlagListItem, ResolveComplianceFlagRequest } from '../../_model/compliance-flag.model';

@Injectable({
  providedIn: 'root'
})
export class ComplianceAdminService {
  private readonly baseUrl = `${environment.apiBaseUrl}/Compliance/admin/config`;
  private readonly complianceUrl = `${environment.apiBaseUrl}/Compliance`;
  private readonly pageSize = environment.pageSize;

  constructor(private http: HttpClient) {}

  private buildParams(
    filters: Record<string, any> = {},
    includePaging = true
  ): HttpParams {
    const merged = includePaging
      ? {
          page: filters['page'] ?? 1,
          pageSize: filters['pageSize'] ?? this.pageSize,
          ...filters
        }
      : { ...filters };

    let params = new HttpParams();

    Object.entries(merged).forEach(([key, value]) => {
      if (value !== null && value !== undefined && value !== '') {
        params = params.set(key, String(value));
      }
    });

    return params;
  }

  private getList<T>(path: string, filters: Record<string, any> = {}): Observable<ApiResponse<T>> {
    return this.http.get<ApiResponse<T>>(`${this.baseUrl}/${path}`, {
      params: this.buildParams(filters)
    });
  }

  private getOne<T>(path: string): Observable<T> {
    return this.http.get<T>(`${this.baseUrl}/${path}`);
  }

  private postOne<T>(path: string, payload: any): Observable<T> {
    return this.http.post<T>(`${this.baseUrl}/${path}`, payload);
  }

  private putOne<T>(path: string, payload: any): Observable<T> {
    return this.http.put<T>(`${this.baseUrl}/${path}`, payload);
  }

  private deleteOne<T>(path: string): Observable<T> {
    return this.http.delete<T>(`${this.baseUrl}/${path}`);
  }

  // =========================
  // Jurisdictions
  // =========================
  getJurisdictions(filters: any = {}): Observable<ApiResponse<ComplianceJurisdictionDto[]>> {
    return this.getList<ComplianceJurisdictionDto[]>('jurisdictions', filters);
  }

  getJurisdictionById(id: string): Observable<ApiResponse<ComplianceJurisdictionDto>> {
    return this.getOne<ApiResponse<ComplianceJurisdictionDto>>(`jurisdictions/${id}`);
  }

  createJurisdiction(payload: any): Observable<ApiResponse<ComplianceJurisdictionDto>> {
    return this.postOne<ApiResponse<ComplianceJurisdictionDto>>('jurisdictions', payload);
  }

  updateJurisdiction(id: string, payload: any): Observable<ApiResponse<ComplianceJurisdictionDto>> {
    return this.putOne<ApiResponse<ComplianceJurisdictionDto>>(`jurisdictions/${id}`, payload);
  }

  // =========================
  // Tiers
  // =========================
  getTiers(filters: any = {}): Observable<ApiResponse<ComplianceTierDto[]>> {
    return this.getList<ComplianceTierDto[]>('tiers', filters);
  }

  getTierById(id: string): Observable<ApiResponse<ComplianceTierDetailsDto>> {
    return this.getOne<ApiResponse<ComplianceTierDetailsDto>>(`tiers/${id}`);
  }

  createTier(payload: any): Observable<ApiResponse<ComplianceTierDetailsDto>> {
    return this.postOne<ApiResponse<ComplianceTierDetailsDto>>('tiers', payload);
  }

  updateTier(id: string, payload: any): Observable<ApiResponse<ComplianceTierDto>> {
    return this.putOne<ApiResponse<ComplianceTierDto>>(`tiers/${id}`, payload);
  }

  // =========================
  // Tier Limits
  // =========================
  getTierLimits(tierId: string): Observable<ApiResponse<ComplianceActivityLimitDto>> {
    return this.getOne<ApiResponse<ComplianceActivityLimitDto>>(`tiers/${tierId}/limits`);
  }

  createTierLimit(tierId: string, payload: any): Observable<ApiResponse<ComplianceActivityLimitDto>> {
    return this.postOne<ApiResponse<ComplianceActivityLimitDto>>(`tiers/${tierId}/limits`, payload);
  }

  updateTierLimit(id: string, payload: any): Observable<ApiResponse<ComplianceActivityLimitDto>> {
    return this.putOne<ApiResponse<ComplianceActivityLimitDto>>(`limits/${id}`, payload);
  }

  deleteTierLimit(id: string): Observable<ApiResponse<ComplianceActivityLimitDto>> {
    return this.deleteOne<ApiResponse<ComplianceActivityLimitDto>>(`limits/${id}`);
  }

  // =========================
  // Existing endpoint helpers
  // =========================
  getAdminTierTable(
    jurisdictionCode: string,
    customerType: number,
    productType: number
  ): Observable<ApiResponse<ComplianceTierTableDto>> {
    const params = this.buildParams(
      { jurisdictionCode, customerType, productType },
      false
    );

    return this.http.get<ApiResponse<ComplianceTierTableDto>>(
      `${this.complianceUrl}/admin/tiers/table`,
      { params }
    );
  }
  
  private readonly complianceFlagsUrl = `${environment.apiBaseUrl}/users/admin/compliance-flags`;
  private readonly complianceDashboardUrl = `${environment.apiBaseUrl}/users/admin/compliance-dashboard`;

  getOpenFlags(): Observable<ApiResponse<ComplianceFlagListItem[]>> {
    return this.http.get<ApiResponse<ComplianceFlagListItem[]>>(this.complianceFlagsUrl);
  }

  resolveFlag(flagId: string, payload: ResolveComplianceFlagRequest): Observable<ApiResponse<ComplianceFlagListItem>> {
    return this.http.post<ApiResponse<ComplianceFlagListItem>>(
      `${this.complianceFlagsUrl}/${flagId}/resolve`,
      payload
    );
  }

  getFlagDashboard(): Observable<ApiResponse<any>> {
    return this.http.get<ApiResponse<any>>(this.complianceDashboardUrl);
  }

  // =========================
  // Lookup helpers
  // =========================
  getCustomerTypes(): LabelValue[] {
    return [
      { label: 'Individual', value: 1 },
      { label: 'Business', value: 2 }
    ];
  }

  getProductTypes(): LabelValue[] {
    return [
      { label: 'Wallet', value: 1 },
      { label: 'Trading', value: 2 },
      { label: 'Remittance', value: 3 }
    ];
  }

  getActivityTypes(): LabelValue[] {
    return [
      { label: 'Deposit', value: 1 },
      { label: 'Withdrawal', value: 2 },
      { label: 'Trade', value: 3 }
    ];
  }

  getTierCodes(): LabelValue[] {
    return [
      { label: 'BASIC', value: 'BASIC' },
      { label: 'ADVANCED', value: 'ADVANCED' },
      { label: 'ENHANCED', value: 'ENHANCED' },
      { label: 'VERIFIED', value: 'VERIFIED' }
    ];
  }

  getBooleanOptions(): LabelValue[] {
    return [
      { label: 'Yes', value: true },
      { label: 'No', value: false }
    ];
  }

  // =========================
  // Policies
  // =========================
  getPolicies(filters: any = {}): Observable<ApiResponse<UserKycPolicySnapshotDto[]>> {
    return this.getList<UserKycPolicySnapshotDto[]>('policies', filters);
  }

  getPolicyById(id: string): Observable<UserKycPolicySnapshotDetailsDto> {
    return this.getOne<UserKycPolicySnapshotDetailsDto>(`policies/${id}`);
  }

  createPolicy(payload: any): Observable<UserKycPolicySnapshotDetailsDto> {
    return this.postOne<UserKycPolicySnapshotDetailsDto>('policies', payload);
  }

  updatePolicy(id: string, payload: any): Observable<UserKycPolicySnapshotDetailsDto> {
    return this.putOne<UserKycPolicySnapshotDetailsDto>(`policies/${id}`, payload);
  }

  // =========================
  // Policy Contents
  // =========================
  getPolicyContents(policyId: string): Observable<any> {
    return this.getOne<any>(`policies/${policyId}/contents`);
  }

  createPolicyContent(policyId: string, payload: any): Observable<any> {
    return this.postOne<any>(`policies/${policyId}/contents`, payload);
  }

  updatePolicyContent(policyId: string, payload: any): Observable<any> {
    return this.putOne<any>(`policies/${policyId}/contents`, payload);
  }

  deletePolicyContent(policyId: string): Observable<any> {
    return this.deleteOne<any>(`policies/${policyId}/contents`);
  }

  // =========================
  // Policy Fields
  // =========================
  getPolicyFields(policyId: string): Observable<any> {
    return this.getOne<any>(`policies/${policyId}/fields`);
  }

  createPolicyField(policyId: string, payload: any): Observable<any> {
    return this.postOne<any>(`policies/${policyId}/fields`, payload);
  }

  updatePolicyField(policyId: string, id: string, payload: any): Observable<any> {
    return this.putOne<any>(`policies/${policyId}/fields/${id}`, payload);
  }

  deletePolicyField(policyId: string, id: string): Observable<any> {
    return this.deleteOne<any>(`policies/${policyId}/fields/${id}`);
  }

  // =========================
  // Policy Documents
  // =========================
  getPolicyDocuments(policyId: string): Observable<any> {
    return this.getOne<any>(`policies/${policyId}/documents`);
  }

  createPolicyDocument(policyId: string, payload: any): Observable<any> {
    return this.postOne<any>(`policies/${policyId}/documents`, payload);
  }

  updatePolicyDocument(policyId: string, id: string, payload: any): Observable<any> {
    return this.putOne<any>(`policies/${policyId}/documents/${id}`, payload);
  }

  deletePolicyDocument(policyId: string, id: string): Observable<any> {
    return this.deleteOne<any>(`policies/${policyId}/documents/${id}`);
  }

  // =========================
  // Policy Rules
  // =========================
  getPolicyRules(policyId: string): Observable<any> {
    return this.getOne<any>(`policies/${policyId}/rules`);
  }

  createPolicyRule(policyId: string, payload: any): Observable<any> {
    return this.postOne<any>(`policies/${policyId}/rules`, payload);
  }

  updatePolicyRule(policyId: string, id: string, payload: any): Observable<any> {
    return this.putOne<any>(`policies/${policyId}/rules/${id}`, payload);
  }

  deletePolicyRule(policyId: string, id: string): Observable<any> {
    return this.deleteOne<any>(`policies/${policyId}/rules/${id}`);
  }

  // =========================
  // Catalog
  // =========================
  getCatalogBootstrap(): Observable<ApiResponse<ComplianceCatalogBootstrapDto>> {
    return this.getOne<ApiResponse<ComplianceCatalogBootstrapDto>>('catalog/bootstrap');
  }

  getFieldCatalog(): Observable<ApiResponse<ComplianceFieldTemplateDto>> {
    return this.getOne<ApiResponse<ComplianceFieldTemplateDto>>('catalog/fields');
  }

  getDocumentCatalog(): Observable<ApiResponse<ComplianceDocumentTemplateDto>> {
    return this.getOne<ApiResponse<ComplianceDocumentTemplateDto>>('catalog/documents');
  }

  getRuleCatalog(): Observable<ApiResponse<ComplianceRuleTemplateDto>> {
    return this.getOne<ApiResponse<ComplianceRuleTemplateDto>>('catalog/rules');
  }

  // =========================
  // Reviews / Dashboard
  // =========================
  getReviews(payload: any): Observable<ApiResponse<any[]>> {
    return this.http.post<ApiResponse<any[]>>(
      `${this.complianceUrl}/admin/reviews/search`,
      payload
    );
  }

  getDashboard(): Observable<ApiResponse<any>> {
    return this.http.get<ApiResponse<any>>(
      `${this.complianceUrl}/admin/reviews/dashboard`
    );
  }

  // =========================
  // Static options
  // =========================
  getPolicyStatuses(): LabelValue[] {
    return [
      { label: 'Draft', value: 1 },
      { label: 'Active', value: 2 },
      { label: 'Retired', value: 3 }
    ];
  }

  getFieldTypes(): LabelValue[] {
    return [
      { label: 'text', value: 'text' },
      { label: 'textarea', value: 'textarea' },
      { label: 'number', value: 'number' },
      { label: 'date', value: 'date' },
      { label: 'select', value: 'select' },
      { label: 'radio', value: 'radio' },
      { label: 'checkbox', value: 'checkbox' },
      { label: 'email', value: 'email' },
      { label: 'phone', value: 'phone' }
    ];
  }
}
